import { Link } from 'react-router-dom';
import { ChevronRightIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import { founderDashboardAPI } from '../../../../services/api';
import {
  PageHeader,
  PageState,
  StaleNotice,
  StatusBadge,
} from './OutreachShared';
import { focusRing, humanize, listFrom, useOutreachResource } from './outreachUtils';

export default function OutreachTemplatesPage() {
  const resource = useOutreachResource(() => founderDashboardAPI.getOutreachTemplates(), []);
  const templates = listFrom(resource.data, 'templates', 'items', 'rows');

  return (
    <div className="space-y-5">
      <PageHeader
        title="Template Studio"
        description="Inspect synthetic fixtures, exact checksums, approvals, and safe internal test sends."
        refreshing={resource.refreshing}
        onRefresh={resource.reload}
      />
      <StaleNotice
        generatedAt={resource.data?.generatedAt || resource.data?.generated_at}
        loadedAt={resource.loadedAt}
      />

      <PageState
        loading={resource.loading}
        error={resource.error}
        permissionDenied={resource.permissionDenied}
        empty={templates.length === 0}
        emptyTitle="No lifecycle templates"
        emptyMessage="Source-controlled templates will appear here after the registry is available."
        onRetry={resource.reload}
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          {templates.map((template) => {
            const key = template.templateKey || template.template_key || template.key;
            const version = template.version || template.templateVersion || template.template_version;
            const approval =
              template.approvalStatus ||
              template.approval_status ||
              (template.approved ? 'approved' : 'pending');
            const variants =
              template.variants ||
              template.fixtureVariants ||
              template.fixture_variants ||
              ['with_name', 'without_name'];

            return (
              <Link
                key={`${key}-${version}`}
                to={`${encodeURIComponent(key)}/${encodeURIComponent(version)}`}
                className={`group rounded-xl border border-gray-200 bg-white p-5 shadow-sm transition hover:border-blue-300 hover:shadow ${focusRing}`}
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-blue-50 p-2 text-blue-600">
                    <DocumentTextIcon className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="font-semibold text-gray-900">
                        {template.name || humanize(key)}
                      </h4>
                      <StatusBadge value={approval} />
                    </div>
                    <p className="mt-1 break-all font-mono text-xs text-gray-500">{key}</p>
                    <p className="mt-3 text-sm text-gray-600">
                      Version {version || '—'} · {variants.length} synthetic variant
                      {variants.length === 1 ? '' : 's'}
                    </p>
                    <p className="mt-2 truncate font-mono text-xs text-gray-500">
                      SHA-256 {template.checksum || template.templateChecksum || template.template_checksum || '—'}
                    </p>
                  </div>
                  <ChevronRightIcon className="h-5 w-5 flex-none text-gray-400 group-hover:text-blue-600" />
                </div>
              </Link>
            );
          })}
        </div>
      </PageState>
    </div>
  );
}
